// FI-2113 full-height swim lanes, shared by the ERM board and the Coaching
// Board. the board fills the viewport below the page chrome and each lane
// scrolls itself, so the page never scrolls vertically and every lane's
// header (and drop target) stays on screen for the whole drag.
//
// the host component provides:
//   - ref="board" on the lane flex container
//   - a `--sl-board-top` consumer in its css:
//       height: calc(100vh - var(--sl-board-top, 320px) - 16px)
//   - @scroll="onLaneScroll(status, $event)" on each lane's scroll wrapper,
//     with the wrapper classes driven by laneShadows[status]
//
// the top offset is measured, not hardcoded: the chrome above the board
// (rail, tenant note, filter rows, the phone filters fold) varies by theme
// and viewport, and measuring one rect beats keeping a constant in sync
// with all of that.
export default {
  data() {
    return {
      // status -> { top: bool, bottom: bool } — is there content scrolled
      // out of view above / below this lane's fold?
      laneShadows: {},
    }
  },
  mounted() {
    this._slvResize = () => {
      // a resize changes the geometry the shave was derived from, so it
      // re-converges from zero rather than carrying a stale value
      this._slvShave = 0
      if (this._slvEl) this._slvEl.style.removeProperty('--sl-board-shave')
      this.syncBoardHeight()
      this.syncLaneShadows()
    }
    window.addEventListener('resize', this._slvResize)
    this.$nextTick(this._slvResize)
  },
  updated() {
    // the board's top edge moves whenever the chrome above it re-renders
    // (filters fold, banners appear). one rect read per patch is cheap, and
    // the write is skipped when the value has not moved. lane shadows also
    // re-derive here: filters and page loads change a lane's scrollHeight
    // without any scroll event.
    this.syncBoardHeight()
    this.syncLaneShadows()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._slvResize)
  },
  methods: {
    syncBoardHeight() {
      const el = this.$refs.board
      if (!el) return
      // the cache lives on the component but the css vars live on the DOM
      // node, and both boards unmount the board element behind the loading
      // spinner (tenant switch, retry) — a fresh element must be re-stamped
      // even when the measured values have not moved
      if (this._slvEl !== el) {
        this._slvEl = el
        this._slvTop = null
        if (this._slvShave) el.style.setProperty('--sl-board-shave', this._slvShave + 'px')
      }
      const top = Math.max(0, Math.round(el.getBoundingClientRect().top + (window.scrollY || 0)))
      if (this._slvTop !== top) {
        this._slvTop = top
        el.style.setProperty('--sl-board-top', top + 'px')
      }
      // the calc can't see chrome BELOW the board (the shell's container
      // padding varies by theme), so shave off whatever page overflow is
      // actually measured. accumulates until the page stops scrolling;
      // bounded so an unrelated tall element can't eat the board, reset on
      // resize, and NEVER fed while the board's min-height clamp is what is
      // overflowing the page — shaving can't fix that, so accumulating
      // would just ratchet to the cap and permanently shorten the board.
      if (this._slvRaf) cancelAnimationFrame(this._slvRaf)
      this._slvRaf = requestAnimationFrame(() => {
        this._slvRaf = null
        if (this._isDestroyed || !this.$refs.board) return
        // 6 mirrors the boards' css gap constant -- change them together
        const expected = window.innerHeight - (this._slvTop || 0) - 6 - (this._slvShave || 0)
        const clamped = el.getBoundingClientRect().height > expected + 2
        if (clamped) return
        const over = document.documentElement.scrollHeight - window.innerHeight
        if (over > 0 && over <= 120) {
          const shave = Math.min(120, (this._slvShave || 0) + over)
          if (shave !== this._slvShave) {
            this._slvShave = shave
            el.style.setProperty('--sl-board-shave', shave + 'px')
          }
        }
      })
    },
    onLaneScroll(status, evt) {
      this._slvApplyShadow(status, evt.target)
    },
    // lanes register their scroll wrapper as :ref="'laneScroll-' + status";
    // this re-reads them all so shadow state survives content changes
    syncLaneShadows() {
      Object.keys(this.$refs).forEach(key => {
        if (!key.startsWith('laneScroll-')) return
        let el = this.$refs[key]
        if (Array.isArray(el)) el = el[0]
        if (el) this._slvApplyShadow(key.slice('laneScroll-'.length), el)
      })
    },
    _slvApplyShadow(status, el) {
      const top = el.scrollTop > 1
      const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 1
      const prev = this.laneShadows[status]
      if (prev && prev.top === top && prev.bottom === bottom) return
      this.$set(this.laneShadows, status, { top, bottom })
    },
    laneShadowClasses(prefix, status) {
      const s = this.laneShadows[status]
      if (!s) return ''
      return {
        [prefix + '--shadow-top']: s.top,
        [prefix + '--shadow-bottom']: s.bottom,
      }
    },
  },
}
